import { useEffect, useRef, useState } from "react";
import api from "../lib/api";

/**
 * Fetches a rotating 4-digit verification code for a ticket.
 * Same ticket + same ~30s window returns the SAME code on both visitor and merchant side
 * (server-side HMAC). Previous codes instantly expire — a screenshot is never reusable.
 *
 * Params:
 *   - ticketId: ticket uuid
 *   - role: "visitor" | "merchant" — picks the right endpoint
 *   - enabled: disable to stop polling (e.g., when ticket is null)
 *
 * Returns:
 *   { code, remainingSec, total, loading, error }
 */
export function useTicketCode(ticketId, role, enabled = true) {
  const [state, setState] = useState({ code: null, remainingSec: null, total: 30, loading: true, error: null });
  const timeoutRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!enabled || !ticketId) {
      setState({ code: null, remainingSec: null, total: 30, loading: false, error: null });
      clearTimeout(timeoutRef.current);
      clearInterval(tickRef.current);
      return;
    }

    let cancelled = false;
    const path = role === "merchant"
      ? `/merchant/tickets/${ticketId}/code`
      : `/tickets/${ticketId}/code`;

    const fetchCode = async () => {
      try {
        const { data } = await api.get(path);
        if (cancelled) return;
        const nowSec = Math.floor(Date.now() / 1000);
        const remaining = Math.max(0, data.windowEnd - nowSec);
        setState({
          code: data.code,
          remainingSec: remaining,
          total: data.windowSeconds,
          loading: false,
          error: null,
        });
        // Refresh 1s after window expires to get the next code
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(fetchCode, (remaining + 1) * 1000);
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, error: err.response?.data?.detail || "Erreur" }));
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(fetchCode, 5000); // retry in 5s
      }
    };

    fetchCode();

    // Local countdown tick every second (purely display — real value comes from server)
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setState((s) => {
        if (s.remainingSec === null) return s;
        return { ...s, remainingSec: Math.max(0, s.remainingSec - 1) };
      });
    }, 1000);

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
      clearInterval(tickRef.current);
    };
  }, [ticketId, role, enabled]);

  return state;
}
